import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Edit, Trash2, Plus, Star } from 'lucide-react';
import {
  StatusDto,
  StatusGroup,
  statusApi,
  handleApiError,
  getStatusTypeName
} from '../../services/statusService';
import StatusEditModal from '../status/StatusEditModal';
import ConfirmDialog from '../ConfirmDialog';
import { toast } from 'react-hot-toast';
import useDragAndDrop from '../../hooks/useDragAndDrop';

const TabContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
`;

const TabHeaders = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-color);
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 16px 32px;
  border: none;
  background: none;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};

  &:hover {
    background: var(--hover-background);
  }

  ${props => props.$active && `
    &:after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--primary-color);
    }
  `}
`;

const TabContent = styled.div`
  padding: 24px;
  min-height: 400px;
`;

const StatusPipeline = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 16px 0;
  align-items: stretch;
  min-height: 300px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 4px;
  }
`;

const StatusCard = styled.div<{ color: string; $isDragging?: boolean }>`
  background: white;
  border-radius: 10px;
  padding: 18px;
  padding-top: 24px;
  min-width: 200px;
  max-width: 240px;
  border: 1px solid var(--border-color);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  user-select: none;
  transition: all 0.2s ease;
  opacity: ${props => props.$isDragging ? 0.7 : 1};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: var(--primary-color);
    border-radius: 10px 10px 0 0;
  }

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 115, 234, 0.15);
  }
`;

const ColorDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  margin-left: 8px;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px white, 0 0 0 3px ${props => props.color}20;
`;

const StatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const StatusName = styled.h3`
  margin: 0 0 6px 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
    color: var(--text-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StatusTypeBadge = styled.span<{ type: number }>`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 500;
  background: ${props => {
    switch(props.type) {
      case 1: return '#e3f2fd'; // Initial
      case 2: return '#e8f5e9'; // Success
      case 3: return '#ffebee'; // Cancelled
      case 4: return '#fff3e0'; // InProgress
      default: return '#f5f5f5'; // Standard
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 1: return '#1976d2'; // Initial
      case 2: return '#388e3c'; // Success
      case 3: return '#c62828'; // Cancelled
      case 4: return '#f57c00'; // InProgress
      default: return '#616161'; // Standard
    }
  }};
`;

const StatusInfo = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 8px;
  line-height: 1.4;
`;

const AddStatusButton = styled.button`
  background: white;
  border: 1px dashed var(--border-color);
  border-radius: 10px;
  padding: 18px;
  padding-top: 24px;
  min-width: 200px;
  max-width: 240px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: var(--border-color);
    border-radius: 10px 10px 0 0;
    transition: background 0.2s ease;
  }

  &:hover {
    border-color: var(--primary-color);
    border-style: solid;
    color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 115, 234, 0.15);

    &::before {
      background: var(--primary-color);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: var(--text-secondary);
`;

const StatusManagementTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState<StatusGroup>(StatusGroup.Lead);
  const [statuses, setStatuses] = useState<StatusDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<StatusDto | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    status: StatusDto | null;
    loading: boolean;
  }>({ isOpen: false, status: null, loading: false });

  useEffect(() => {
    loadStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const data = await statusApi.getByGroup(activeTab, true);
      setStatuses(data.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = useCallback(async (newItems: StatusDto[]) => {
    // Update sort order
    const orderMap: { [key: number]: number } = {};
    newItems.forEach((item, index) => {
      orderMap[item.id] = index;
    });

    setStatuses(newItems);

    try {
      await statusApi.reorder(activeTab, orderMap);
      toast.success('סדר הסטטוסים עודכן בהצלחה');
    } catch (error) {
      toast.error(handleApiError(error));
      loadStatuses(); // Reload on error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Drag and drop hook
  const {
    items: orderedStatuses,
    draggedIndex,
    getDraggableProps
  } = useDragAndDrop({
    items: statuses,
    onReorder: handleReorder,
    direction: 'horizontal'
  });

  const handleAddStatus = () => {
    setSelectedStatus(null);
    setShowEditModal(true);
  };

  const handleEditStatus = (status: StatusDto) => {
    setSelectedStatus(status);
    setShowEditModal(true);
  };

  const handleDeleteStatus = async (status: StatusDto) => {
    // First check if can delete
    try {
      const canDeleteResult = await statusApi.canDelete(status.id);
      if (!canDeleteResult.canDelete) {
        // Show detailed error message
        let errorMessage = canDeleteResult.message || 'לא ניתן למחוק סטטוס זה';

        // If status is in use, show more descriptive message
        if (canDeleteResult.usageCount > 0) {
          const entityType = status.group === StatusGroup.Lead ? 'לידים' : 'לקוחות';
          errorMessage = `לא ניתן למחוק את הסטטוס "${status.name}".\n` +
                        `הסטטוס הוא הסטטוס הנוכחי של ${canDeleteResult.usageCount} ${entityType}.\n` +
                        `כדי למחוק סטטוס זה, יש קודם לשנות את הסטטוס של ה${entityType} הללו.`;
        }

        toast.error(errorMessage, {
          duration: 5000, // Show for longer to let user read
          style: {
            maxWidth: '500px',
            textAlign: 'right'
          }
        });
        return;
      }

      // If can delete, show confirmation dialog
      setConfirmDialog({
        isOpen: true,
        status,
        loading: false
      });
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const confirmDelete = async () => {
    if (!confirmDialog.status) return;

    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      await statusApi.delete(confirmDialog.status.id);
      toast.success('הסטטוס נמחק בהצלחה');
      setConfirmDialog({ isOpen: false, status: null, loading: false });
      loadStatuses();
    } catch (error) {
      toast.error(handleApiError(error));
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSaveStatus = async (statusData: any) => {
    try {
      if (selectedStatus) {
        await statusApi.update(selectedStatus.id, statusData);
        toast.success('הסטטוס עודכן בהצלחה');
      } else {
        await statusApi.create({ ...statusData, group: activeTab });
        toast.success('הסטטוס נוצר בהצלחה');
      }
      setShowEditModal(false);
      loadStatuses();
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  return (
    <>
      <TabContainer>
        <TabHeaders>
          <Tab
            $active={activeTab === StatusGroup.Lead}
            onClick={() => setActiveTab(StatusGroup.Lead)}
          >
            סטטוסי לידים
          </Tab>
          <Tab
            $active={activeTab === StatusGroup.Client}
            onClick={() => setActiveTab(StatusGroup.Client)}
          >
            סטטוסי לקוחות
          </Tab>
        </TabHeaders>

        <TabContent>
          {loading ? (
            <LoadingContainer>טוען סטטוסים...</LoadingContainer>
          ) : (
            <StatusPipeline>
              {orderedStatuses.map((status, index) => (
                <StatusCard
                  key={status.id}
                  color={status.color}
                  $isDragging={draggedIndex === index}
                  {...getDraggableProps(index)}
                >
                  <StatusHeader>
                    <div>
                      <StatusName>
                        <ColorDot color={status.color} />
                        {status.name}
                      </StatusName>
                      <StatusTypeBadge type={status.statusType}>
                        {getStatusTypeName(status.statusType)}
                      </StatusTypeBadge>
                    </div>
                    <StatusActions>
                      <ActionButton onClick={() => handleEditStatus(status)} title="עריכה">
                        <Edit size={14} />
                      </ActionButton>
                      {!status.isSystemDefault && (
                        <ActionButton onClick={() => handleDeleteStatus(status)} title="מחיקה">
                          <Trash2 size={14} />
                        </ActionButton>
                      )}
                    </StatusActions>
                  </StatusHeader>

                  {status.description && (
                    <StatusInfo>{status.description}</StatusInfo>
                  )}

                  {status.expectedDurationDays && (
                    <StatusInfo>
                      משך צפוי: {status.expectedDurationDays} ימים
                    </StatusInfo>
                  )}

                  {status.isDefault && (
                    <StatusInfo style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Star size={12} fill="#f59e0b" color="#f59e0b" />
                      סטטוס ברירת מחדל
                    </StatusInfo>
                  )}
                </StatusCard>
              ))}

              <AddStatusButton onClick={handleAddStatus}>
                <Plus size={16} />
                הוסף סטטוס חדש
              </AddStatusButton>
            </StatusPipeline>
          )}
        </TabContent>
      </TabContainer>

      {showEditModal && (
        <StatusEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveStatus}
          status={selectedStatus}
          group={activeTab}
          existingStatuses={statuses}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, status: null, loading: false })}
        onConfirm={confirmDelete}
        title="מחיקת סטטוס"
        message={`האם אתה בטוח שברצונך למחוק את הסטטוס "${confirmDialog.status?.name}"?`}
        type="danger"
        loading={confirmDialog.loading}
      />
    </>
  );
};

export default StatusManagementTab;